import {IssueRelativeTime} from '@github-ui/ago'
import type {HovercardAttributes, HovercardAttributesWithTracking} from '@github-ui/hovercards'
import {hovercardAttributesForActor} from '@github-ui/hovercards'
import {ListItemDescriptionItem} from '@github-ui/list-view/ListItemDescriptionItem'
import {useListViewVariant} from '@github-ui/list-view/ListViewVariantContext'
import {ProfileReference} from '@github-ui/profile-reference'
import {Link, Truncate} from '@primer/react'
import {graphql, useFragment} from 'react-relay'

import {LABELS} from '../constants/labels'
import {TEST_IDS} from '../constants/test-ids'
import {VALUES} from '../constants/values'
import type {CheckRunStatusFromPullRequest$key} from './__generated__/CheckRunStatusFromPullRequest.graphql'
import type {IssuePullRequestDescription$key} from './__generated__/IssuePullRequestDescription.graphql'
import type {IssueRowSecondaryQuery$data} from './__generated__/IssueRowSecondaryQuery.graphql'
import {AgentSuggestionsIndicator} from './AgentSuggestionsIndicator'
import {LazyCheckRunStatus} from './CheckRunStatus'
import styles from './IssueItem.module.css'
import pullRequestDescriptionStyles from './IssuePullRequestDescription.module.css'
import {MilestoneMetadata} from './MilestoneMetadata'
import {LazyReviewDecision} from './ReviewDecision'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const descriptionFragment = graphql`
  fragment IssuePullRequestDescription on IssueOrPullRequest
  @argumentDefinitions(includeMilestone: {type: "Boolean!", defaultValue: true}) {
    ... on Issue {
      createdAt
      updatedAt
      closed
      closedAt

      author {
        login
        resourcePath
        __typename
        ... on Bot {
          isCopilot
          isAgent
        }
        ... on User {
          name
        }
        ... on EnterpriseUserAccount {
          name
        }
        ... on Mannequin {
          name
        }
      }
      number
      stateReason
      pendingSuggestionsCount
      milestone @include(if: $includeMilestone) {
        title
        url
      }
    }

    ... on PullRequest {
      createdAt
      updatedAt
      closed
      closedAt
      author {
        login
        resourcePath
        __typename
        ... on Bot {
          isCopilot
          isAgent
        }
        ... on User {
          name
        }
        ... on EnterpriseUserAccount {
          name
        }
        ... on Mannequin {
          name
        }
      }
      number
      milestone @include(if: $includeMilestone) {
        title
        url
      }
    }
  }
`

type DescriptionProps = {
  dataKey: IssuePullRequestDescription$key | CheckRunStatusFromPullRequest$key
  listItemMetadata?: IssueRowSecondaryQuery$data | null
  repositoryOwner: string
  repositoryName: string
  showRepository?: boolean
  sortingItemSelected?: string
  getAuthorHref?: (login: string) => string
  id: string
}

export function IssuePullRequestDescription({dataKey, repositoryOwner, repositoryName, ...props}: DescriptionProps) {
  const {number} = useFragment(descriptionFragment, dataKey as IssuePullRequestDescription$key)
  const nameWithOwner = `${repositoryOwner}/${repositoryName}`
  const ariaLabels: {[id: string]: string} = {
    number: `number ${number} `,
    repo: `In ${nameWithOwner};`,
  }

  const defaultRepositoryRender = (
    <div className={styles.defaultRepoContainer}>
      <span>{props.showRepository ? nameWithOwner : ''}</span>
      <span className="sr-only">{ariaLabels.number}</span>
    </div>
  )

  const defaultMetaRender = (
    <span className={styles.defaultNumberDescription}>
      <span>#{number}</span>
      &nbsp;
      <span className="sr-only">{ariaLabels.repo}</span>
    </span>
  )

  return (
    <IssuePullRequestDescriptionItem
      dataKey={dataKey}
      defaultRepositoryRender={defaultRepositoryRender}
      defaultMetaRender={defaultMetaRender}
      nameWithOwner={nameWithOwner}
      repositoryOwner={repositoryOwner}
      repositoryName={repositoryName}
      ariaLabels={ariaLabels}
      {...props}
    />
  )
}

const IssuePullRequestDescriptionItem = ({
  dataKey,
  listItemMetadata,
  showRepository = true,
  sortingItemSelected,
  defaultRepositoryRender,
  defaultMetaRender,
  nameWithOwner,
  ariaLabels,
  getAuthorHref,
  id,
}: DescriptionProps & {
  defaultRepositoryRender: React.ReactNode
  defaultMetaRender: React.ReactNode
  nameWithOwner: string
  ariaLabels: {[id: string]: string}
}) => {
  const descriptionData = useFragment(descriptionFragment, dataKey as IssuePullRequestDescription$key)

  const {variant} = useListViewVariant()

  const {
    author,
    closed,
    closedAt: closedAtString,
    createdAt: createdAtString,
    updatedAt: updatedAtString,
    stateReason,
    milestone,
  } = descriptionData

  const authorType = author?.__typename
  const authorLogin = author?.login || VALUES.ghostUserLogin
  const authorFullLogin = authorType === 'Bot' ? `app/${authorLogin}` : authorLogin
  const isCopilot = author?.isCopilot
  const isAgent = author?.isAgent
  const isNonAgentBot = authorType === 'Bot' && !isCopilot && !isAgent
  const authorName = author?.name
  const displayName = isCopilot ? VALUES.copilotDisplayName : authorLogin
  const resourcePath = author?.resourcePath || ''
  const hovercardAttributes = isNonAgentBot
    ? {}
    : hovercardAttributesForActor(authorLogin, {isCopilot, isAgent: isAgent ?? false})
  const createdAt = createdAtString ?? undefined
  const updatedAt = updatedAtString ?? undefined
  const closedAt = closedAtString ?? undefined
  return (
    <ListItemDescriptionItem
      data-testid={TEST_IDS.listRowRepoNameAndNumber}
      className={pullRequestDescriptionStyles.descriptionItem}
    >
      {variant === 'compact'
        ? showRepository && (
            <>
              <Truncate title={nameWithOwner} className={pullRequestDescriptionStyles.repositoryNameTruncate}>
                <span className={styles.compactNameWithOwnerLabel}>{nameWithOwner}</span>
              </Truncate>
              <span className="sr-only">{ariaLabels.number}</span>
            </>
          )
        : showRepository && defaultRepositoryRender}
      {defaultMetaRender}
      {variant === 'default' && (
        <>
          {closed ? (
            <TimestampContainer
              displayName={displayName}
              authorFullLogin={authorFullLogin}
              authorName={authorName}
              resourcePath={resourcePath}
              getAuthorHref={getAuthorHref}
              hovercardAttributes={hovercardAttributes}
              timestamp={closedAt}
              testId="closed-at"
              action={ActionType.Closed}
              isAgent={isCopilot ?? false}
            />
          ) : (
            <TimestampContainer
              displayName={displayName}
              authorFullLogin={authorFullLogin}
              authorName={authorName}
              resourcePath={resourcePath}
              getAuthorHref={getAuthorHref}
              hovercardAttributes={hovercardAttributes}
              timestamp={createdAt}
              testId="created-at"
              action={ActionType.Opened}
              isAgent={isCopilot ?? false}
            />
          )}
          {(sortingItemSelected === LABELS.RecentlyUpdated || sortingItemSelected === 'updated') && updatedAt && (
            <div data-testid="updated-at" className={styles.timestampContainer}>
              {' '}
              &middot; Updated{' '}
              <IssueRelativeTime datetime={updatedAt} className={pullRequestDescriptionStyles.RelativeTime} />
            </div>
          )}
        </>
      )}
      {stateReason?.toLowerCase() === 'duplicate' && <span data-testid="state-reason"> &middot;{' Duplicate'}</span>}
      <LazyReviewDecision id={id} variant={variant} secondaryQueryRef={listItemMetadata} />
      {milestone && <MilestoneMetadata title={milestone.title} url={milestone.url} />}
      <LazyCheckRunStatus id={id} variant={variant} secondaryQueryRef={listItemMetadata} />
      <AgentSuggestionsIndicator count={descriptionData.pendingSuggestionsCount ?? 0} />
    </ListItemDescriptionItem>
  )
}

const ActionType = {
  Opened: 'opened',
  Closed: 'closed',
} as const

type ActionType = (typeof ActionType)[keyof typeof ActionType]

const TimestampContainer = ({
  displayName,
  authorFullLogin,
  authorName,
  resourcePath,
  getAuthorHref,
  hovercardAttributes,
  timestamp,
  testId,
  action,
  isAgent,
}: {
  displayName: string
  authorFullLogin: string
  authorName: string | null | undefined
  resourcePath: string
  getAuthorHref?: (resourcePath: string) => string
  hovercardAttributes: HovercardAttributes | HovercardAttributesWithTracking | Record<string, never>
  timestamp?: string
  testId: string
  action: ActionType
  isAgent: boolean
}) => (
  <div data-testid={testId} className={styles.timestampContainer}>
    <span>&middot; </span>
    <span>{action === ActionType.Closed && ' by '}</span>
    <Link
      inline
      href={getAuthorHref ? getAuthorHref(authorFullLogin) : resourcePath}
      className={styles.authorCreatedLink}
      {...hovercardAttributes}
    >
      <ProfileReference login={displayName} profileName={authorName} isAgent={isAgent} />
    </Link>{' '}
    <span>{action === ActionType.Opened ? ` opened ` : ` was closed `}</span>
    {timestamp && <IssueRelativeTime datetime={timestamp} />}
  </div>
)
